// src/components/quizzes/SortingConveyor.js
//
// ===========================================================================
// SORTING CONVEYOR
// ===========================================================================
// Items ride a belt toward a row of labeled bins. Tap an item to lift it off
// the belt, then tap the bin it belongs in.
//
// WHY THIS EXISTS ALONGSIDE DRAG AND DROP
// ---------------------------------------
// It reads the identical data — questions[].options with { id, text,
// targetText } — so every drag-and-drop topic already in Firestore works here
// with no authoring at all. What changes is the input.
//
// HTML5 drag and drop is a poor fit for the age group this product serves. It
// needs a press, a sustained hold, a controlled move and a release on target,
// and a 6-year-old's hands do not reliably produce that sequence. On a
// touchscreen it is worse: the native dragstart event does not fire at all on
// most mobile browsers, and on an interactive whiteboard a drag is often read
// as a scroll. Two taps is the same sorting skill with none of that.
//
// DESIGN DECISIONS THAT ARE DELIBERATE, NOT PLACEHOLDERS
// ------------------------------------------------------
//   The belt STOPS while an item is held. Thinking time is free. A child who
//   needs to sound out "evaporation" is not punished for it, and the arcade
//   pacing is there to make the round feel alive, not to add time pressure to
//   a comprehension task.
//
//   A wrong bin costs nothing but a shake, and the item goes back on the belt.
//   Losing the item on a wrong answer would teach guessing-avoidance rather
//   than sorting. The attempt IS recorded, so the educator report still shows
//   what the child actually found hard.
//
//   Nothing is ever lost off the end of the belt. Unsorted items loop around.
//   The round ends when every item is placed, so the score reflects sorting
//   accuracy rather than reaction speed.
//
//   Bins are derived from the data, not configured. Whatever distinct
//   targetText values a question contains become the bins, in first-seen
//   order.
// ===========================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { playGameSound } from "../../services/gameSounds";
import { useNavigate, useParams } from "react-router-dom";
import { launchConfetti } from "../../utils/confetti";
import "./SortingConveyor.css";
import { useLeaveGame } from "./gameExit";
import "./kit/quizkit.css"; // .qk-scene: the shared game background (no pirate ship — that's the bonus round's)
import "./kit/classicGames.css"; // the new look for this game (buttons, cards, score chips)

const POINTS_CORRECT = 10;

// How long an item takes to travel the belt once. Slow enough to read a long
// label, and it pauses whenever an item is held anyway.
const BELT_SECONDS = 14;

/* ------------------------------------------------------------------ */
/* Speech                                                              */
/* ------------------------------------------------------------------ */

// Deliberately minimal, and deliberately NOT the shared service.
//
// Read-aloud across this codebase is currently unreliable, and the diagnosis
// is unfinished. So this does only the things known to be safe: it speaks
// straight from the tap with no cancel() beforehand and no timer in between,
// because cancel() followed by speak() in the same tick is silently discarded
// by Chrome, and a delay pushes the call outside the user-activation window.
// A voice is attached only if getVoices() already has one — waiting for
// voiceschanged would introduce exactly the delay being avoided.
//
// When the shared speech service is finished, this should be replaced by it.
const speakLabel = (text) => {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  if (!text) return;

  try {
    const utterance = new SpeechSynthesisUtterance(String(text));
    utterance.lang = "en-US";
    utterance.rate = 0.95;

    const voices = window.speechSynthesis.getVoices() || [];
    const preferred =
      voices.find((v) => /^en-US/i.test(v.lang) && /premium|enhanced|siri/i.test(v.name)) ||
      voices.find((v) => /^en-US/i.test(v.lang));
    if (preferred) utterance.voice = preferred;

    window.speechSynthesis.speak(utterance);
  } catch {
    // A silent label is a small loss. A thrown error mid-round is not.
  }
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const SortingConveyor = ({
  questions = [],
  onComplete,
  topic,
  placeholderImage,
}) => {
  const navigate = useNavigate();
  const leaveGame = useLeaveGame();
  const { subject } = useParams();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [held, setHeld] = useState(null);        // item id currently lifted
  const [placed, setPlaced] = useState({});      // itemId -> bin label
  const [wrongBin, setWrongBin] = useState(null);
  const [poppedBin, setPoppedBin] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Every attempt, right or wrong, for the educator report.
  const attemptsRef = useRef([]);

  const current = questions[currentIndex] || null;

  /* ---------------- derived ---------------- */

  const items = useMemo(() => {
    const raw = current?.options || [];
    // An id is required — it is how a placement is tracked. Topic data
    // supplies one; fall back to the index so a malformed topic degrades to a
    // playable round instead of a blank belt.
    return raw.map((option, i) => ({
      id: option.id ?? `item-${i}`,
      text: option.text ?? "",
      target: option.targetText ?? "",
      image: option.image || null,
    }));
  }, [current]);

  const bins = useMemo(() => {
    const seen = [];
    items.forEach((item) => {
      if (item.target && !seen.includes(item.target)) seen.push(item.target);
    });
    return seen;
  }, [items]);

  const remaining = useMemo(
    () => items.filter((item) => !placed[item.id]),
    [items, placed]
  );

  const allPlaced = items.length > 0 && remaining.length === 0;

  /* ---------------- round flow ---------------- */

  const finish = useCallback(
    (finalScore) => {
      if (completed) return;
      setCompleted(true);

      const attempts = attemptsRef.current;
      const correctCount = attempts.filter((a) => a.correct).length;
      if (correctCount >= Math.ceil(attempts.length * 0.8)) launchConfetti();

      onComplete?.({
        score: finalScore,
        // One row per attempt, in the shape the classroom report renders.
        answers: attempts.map((a) => ({
          question: a.itemText,
          selectedText: a.chosenBin,
          correctText: a.correctBin,
          correct: a.correct,
        })),
        details: [
          {
            gameType: "sortingConveyor",
            score: finalScore,
            attempts: attempts.length,
            correct: correctCount,
          },
        ],
      });
    },
    [completed, onComplete]
  );

  useEffect(() => {
    if (!allPlaced || completed) return;

    // Let the last bin-pop animation land before moving on.
    const timer = setTimeout(() => {
      if (currentIndex + 1 < questions.length) {
        setCurrentIndex((i) => i + 1);
        setPlaced({});
        setHeld(null);
      } else {
        finish(score);
      }
    }, 900);

    return () => clearTimeout(timer);
  }, [allPlaced, completed, currentIndex, questions.length, score, finish]);

  /* ---------------- interaction ---------------- */

  const handleItemTap = (item) => {
    if (completed) return;

    // Tapping the held item puts it back — a child who changes their mind
    // needs an undo that is not "guess a bin".
    if (held === item.id) {
      setHeld(null);
      return;
    }

    setHeld(item.id);
    speakLabel(item.text);
  };

  const handleBinTap = (bin) => {
    if (completed) return;

    if (!held) {
      // Tapping a bin with empty hands reads it aloud. This is how a child
      // checks what a bin is called before committing an item to it.
      speakLabel(bin);
      return;
    }

    const item = items.find((i) => i.id === held);
    if (!item) return;

    const correct = item.target === bin;

    attemptsRef.current.push({
      itemText: item.text,
      chosenBin: bin,
      correctBin: item.target,
      correct,
    });

    playGameSound(correct ? "correct" : "wrong", "sortingConveyor");
    if (correct) {
      setPlaced((prev) => ({ ...prev, [item.id]: bin }));
      setScore((s) => s + POINTS_CORRECT);
      setHeld(null);
      setPoppedBin(bin);
      setTimeout(() => setPoppedBin(null), 500);
    } else {
      // The item returns to the belt. No points lost, no item lost.
      setHeld(null);
      setWrongBin(bin);
      setTimeout(() => setWrongBin(null), 500);
    }
  };

  /* ---------------- guards ---------------- */

  if (!current || !items.length) {
    return (
      <div className="sc-wrapper sc-empty-state">
        <p>This topic has no sorting items yet.</p>
        <p className="sc-empty-sub">
          Sorting Conveyor reads the same options a drag-and-drop topic uses.
          Add options with a text and a targetText and this round will fill in.
        </p>
      </div>
    );
  }

  const heldItem = items.find((i) => i.id === held) || null;

  return (
    <div
      className="sc-wrapper qk-scene"
    >
      {/* ---------------- HUD ---------------- */}
      <div className="sc-hud">
        <div className="sc-hud-item">
          <span>Score</span>
          <strong>{score}</strong>
        </div>
        <div className="sc-hud-item">
          <span>Round</span>
          <strong>
            {currentIndex + 1} / {questions.length}
          </strong>
        </div>
        <div className="sc-hud-item">
          <span>Left</span>
          <strong>{remaining.length}</strong>
        </div>
        <button
          type="button"
          className="sc-exit"
          onClick={() => setShowConfirm(true)}
          aria-label="Leave the game"
        >
          ✕
        </button>
      </div>

      <h2 className="sc-prompt">
        {current.question || "Sort each item into the bin where it belongs."}
      </h2>

      <p className="sc-help">
        {heldItem
          ? `Holding “${heldItem.text}” — now tap a bin.`
          : "Tap an item to pick it up."}
      </p>

      {/* ---------------- belt ----------------
          The belt pauses whenever an item is held. A CSS animation-play-state
          toggle does that in one class, and unlike a JS loop it keeps every
          item's position when it resumes rather than restarting them. */}
      <div className={`sc-belt ${held ? "is-paused" : ""}`}>
        <div className="sc-belt-surface" aria-hidden="true" />

        <div className="sc-belt-track">
          {remaining.map((item, i) => (
            <button
              type="button"
              key={item.id}
              className={`sc-item ${held === item.id ? "is-held" : ""}`}
              style={{
                // Spread the items evenly around the loop so they do not
                // arrive in a clump.
                animationDelay: `${(-BELT_SECONDS / Math.max(remaining.length, 1)) * i}s`,
                animationDuration: `${BELT_SECONDS}s`,
              }}
              onClick={() => handleItemTap(item)}
              aria-pressed={held === item.id}
            >
              {item.image && (
                <img src={item.image} alt="" className="sc-item-img" />
              )}
              <span className="sc-item-text">{item.text}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ---------------- bins ---------------- */}
      <div className="sc-bins" style={{ "--sc-bin-count": bins.length }}>
        {bins.map((bin) => {
          const contents = items.filter((item) => placed[item.id] === bin);

          return (
            <button
              type="button"
              key={bin}
              className={`sc-bin
                ${wrongBin === bin ? "is-wrong" : ""}
                ${poppedBin === bin ? "is-pop" : ""}
                ${held ? "is-ready" : ""}
              `}
              onClick={() => handleBinTap(bin)}
            >
              <span className="sc-bin-label">{bin}</span>

              <span className="sc-bin-contents">
                {contents.map((item) => (
                  <span key={item.id} className="sc-chip">
                    {item.text}
                  </span>
                ))}
              </span>

              <span className="sc-bin-count">{contents.length}</span>
            </button>
          );
        })}
      </div>

      {/* ---------------- exit confirm ---------------- */}
      {showConfirm && (
        <div className="sc-confirm">
          <div className="sc-confirm-card">
            <p>Leave this game?</p>
            <p className="sc-confirm-sub">Your score for this round will not be saved.</p>
            <div className="sc-confirm-actions">
              <button
                type="button"
                className="sc-btn"
                onClick={() => setShowConfirm(false)}
              >
                Keep playing
              </button>
              <button
                type="button"
                className="sc-btn sc-btn-quiet"
                onClick={() => leaveGame({ ask: false }) /* already asked above */}
              >
                Leave
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="sc-footer">
        © Smart Board Learning LLC. All rights reserved.
      </div>
    </div>
  );
};

export default SortingConveyor;
